import React, { useState } from 'react';
import { getSupabase } from '../utils/SupabaseClient';
import { getCachedGeo } from '../utils/Tracker';
import SoundManager from '../utils/SoundManager';

const SESSION_KEY = 'gb4k_session_id_v1';

const MessageVisualization: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) {
      setErrorMsg('Message body is required.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErrorMsg('');

    const supa = getSupabase();
    if (!supa) {
      setStatus('error');
      setErrorMsg('Supabase is not configured. Message could not be sent.');
      return;
    }

    let sessionId: string | null = null;
    try { sessionId = sessionStorage.getItem(SESSION_KEY); } catch {}

    const geo = getCachedGeo() ?? {};

    const { error } = await supa.from('gb4k_fr_messages').insert({
      session_id: sessionId,
      name: name.trim() || null,
      email: email.trim() || null,
      body: trimmed,
      ip: geo.ip ?? null,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      city: geo.city ?? null,
      user_agent: navigator.userAgent,
    });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      try { SoundManager.playSound('error'); } catch {}
      return;
    }
    setStatus('sent');
    try { SoundManager.playSound('command'); } catch {}
    setName('');
    setEmail('');
    setBody('');
  };

  return (
    <div className="w-full h-full overflow-y-auto p-4 text-[#5fe979] font-mono">
      <div className="max-w-xl mx-auto">
        <h3 className="text-lg font-bold text-[#7aff94] mb-1">LEAVE A MESSAGE</h3>
        <p className="text-xs opacity-70 mb-6">
          A direct line to me. Name and email are optional. The message
          itself is required. You'll get no reply if you don't leave an
          email — that's by design.
        </p>

        {status === 'sent' ? (
          <div className="border border-[#7aff94] p-4 rounded">
            <div className="text-[#7aff94] font-bold mb-1">✓ MESSAGE SENT</div>
            <p className="text-sm opacity-80">
              Thank you. I'll read it. If you left an email, expect a reply
              within a few days.
            </p>
            <button
              className="mt-3 px-3 py-1 border border-[#5fe979] text-xs hover:bg-[#132a1b]"
              onClick={() => setStatus('idle')}
            >
              SEND ANOTHER
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs opacity-70 mb-1">NAME (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-black border border-[var(--terminal-border)] p-2 outline-none focus:border-[#5fe979] font-mono text-sm"
                placeholder="(your name)"
                maxLength={120}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <label className="block text-xs opacity-70 mb-1">EMAIL (optional — required for a reply)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-[var(--terminal-border)] p-2 outline-none focus:border-[#5fe979] font-mono text-sm"
                placeholder="you@somewhere"
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <label className="block text-xs opacity-70 mb-1">MESSAGE</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full bg-black border border-[var(--terminal-border)] p-2 outline-none focus:border-[#5fe979] font-mono text-sm"
                placeholder="Write whatever you want."
                rows={8}
                maxLength={5000}
                spellCheck={false}
              />
              <div className="text-xs opacity-50 mt-1 text-right">{body.length} / 5000</div>
            </div>

            {status === 'error' && (
              <div className="text-red-400 text-sm border border-red-400 p-2 rounded">
                {errorMsg || 'Something went wrong.'}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full px-4 py-2 bg-[#245032] text-[#5fe979] border border-[#5fe979] hover:bg-[#132a1b] disabled:opacity-50 font-bold text-sm"
            >
              {status === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default MessageVisualization;
